/**
 * Los datos del formulario de checkout, guardados en el navegador.
 *
 * Sin esto, un pago rechazado en Webpay volvia al checkout con el formulario
 * en blanco: entre que se aprieta "Pagar" y se vuelve de la pasarela hay una
 * navegacion completa fuera del sitio, y React pierde todo su estado en el
 * camino — el carro sobrevive porque ya estaba en `localStorage` (CHK-02);
 * los datos de contacto, entrega y documento no, porque vivian solo en el
 * estado del componente. Mismo criterio que `almacen-carro.ts`: se guarda en
 * cada cambio y se valida campo por campo al leer, porque puede venir de una
 * version anterior del sitio o de alguien que edito el almacenamiento a
 * mano. Lo que no calza se descarta entero — nunca un formulario a medio
 * llenar con campos inventados.
 */
import type { DatosCheckout } from './checkout';
import { CHECKOUT_INICIAL } from './checkout';

const CLAVE_CHECKOUT = 'elcarpintero:checkout';

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null;
}

function esDatosCheckout(valor: unknown): valor is DatosCheckout {
  return (
    esObjeto(valor) &&
    typeof valor['nombre'] === 'string' &&
    typeof valor['email'] === 'string' &&
    typeof valor['telefono'] === 'string' &&
    (valor['tipoEntrega'] === 'despacho' || valor['tipoEntrega'] === 'retiro') &&
    typeof valor['codigoRegion'] === 'string' &&
    typeof valor['comuna'] === 'string' &&
    typeof valor['calle'] === 'string' &&
    typeof valor['numero'] === 'string' &&
    typeof valor['departamento'] === 'string' &&
    typeof valor['referencia'] === 'string' &&
    (valor['tipoDocumento'] === 'boleta' || valor['tipoDocumento'] === 'factura') &&
    typeof valor['rutComprador'] === 'string' &&
    typeof valor['rut'] === 'string' &&
    typeof valor['razonSocial'] === 'string' &&
    typeof valor['giro'] === 'string' &&
    typeof valor['facturacionIgualDespacho'] === 'boolean' &&
    typeof valor['codigoRegionFacturacion'] === 'string' &&
    typeof valor['comunaFacturacion'] === 'string' &&
    typeof valor['calleFacturacion'] === 'string' &&
    typeof valor['numeroFacturacion'] === 'string' &&
    typeof valor['departamentoFacturacion'] === 'string' &&
    typeof valor['aceptaPromociones'] === 'boolean'
  );
}

export function leerCheckoutGuardado(): DatosCheckout {
  let crudo: string | null = null;

  try {
    crudo = window.localStorage.getItem(CLAVE_CHECKOUT);
  } catch {
    return CHECKOUT_INICIAL;
  }

  if (crudo === null) return CHECKOUT_INICIAL;

  try {
    const leido: unknown = JSON.parse(crudo);
    if (!esObjeto(leido)) return CHECKOUT_INICIAL;

    // Los campos que se agregaron despues (el RUT con boleta, 16-09-2026; la
    // direccion de facturacion, 29-09-2026) no
    // estan en un formulario guardado antes: se completan con su valor vacio
    // en vez de descartar todo lo que la persona ya habia escrito. No es
    // inventar datos — vacio es exactamente lo que el campo tendria si recien
    // apareciera — y la validacion de abajo sigue siendo estricta con el resto.
    const datos: unknown = { ...CHECKOUT_INICIAL, ...leido };
    return esDatosCheckout(datos) ? datos : CHECKOUT_INICIAL;
  } catch {
    // JSON roto: se descarta entero en vez de arrastrar un formulario a medias.
    return CHECKOUT_INICIAL;
  }
}

export function guardarCheckout(datos: DatosCheckout): void {
  try {
    window.localStorage.setItem(CLAVE_CHECKOUT, JSON.stringify(datos));
  } catch {
    // Sin almacenamiento los datos duran lo que dure la pestana. No es un error.
  }
}

/** Se llama cuando el pago ya se aprobo (PAG-02): no hay razon para seguir ofreciendo estos datos. */
export function borrarCheckoutGuardado(): void {
  try {
    window.localStorage.removeItem(CLAVE_CHECKOUT);
  } catch {
    // Sin almacenamiento no hay nada que borrar.
  }
}
